import {Form, Formik} from 'formik';
import {FormikHelpers} from 'formik/dist/types';
import {useTranslation} from 'next-i18next';
import {MouseEventHandler} from 'react';
import * as Yup from 'yup';

import {ClinicInfoSettingsTypes} from '@/src/profile/accountSettings/containers/types/ClinicInfoSettingsTypes';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Button, Input, Separator, Text} from '../../../ui';
import css from '../styles/EditFullNamePopupLayout.module.css';

interface EditClinicInfoPopupLayoutProps extends ClinicInfoSettingsTypes {
  onSubmitClick: (
    values,
    formikHelpers: FormikHelpers<any>
  ) => void | Promise<any>;
  onCancelClick: MouseEventHandler<HTMLDivElement>;
}

const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 100;

const getValidationSchema = (t) => {
  return Yup.object().shape({
    clinicName: Yup.string()
      .min(
        MIN_NAME_LENGTH,
        t('validation.min-length-error', {
          minLength: MIN_NAME_LENGTH,
        })
      )
      .max(
        MAX_NAME_LENGTH,
        t('validation.max-length-error', {
          maxLength: MAX_NAME_LENGTH,
        })
      )
      .required(t('validation.field-req')),
    userName: Yup.string().required(t('validation.field-req')).nullable(),
    email: Yup.string().email().required(t('validation.field-req')).nullable(),
    website: Yup.string()
      .matches(
        /^(https?:\/\/)?(www\.)?[a-z0-9]+\.[a-z]{2,}(\/.*)?$/,
        t('validation.website_is_invalid')
      )
      .nullable(),
  });
};

const EditClinicInfoPopupLayout = ({
  clinicName,
  userName,
  email,
  website,
  onSubmitClick,
  onCancelClick,
}: EditClinicInfoPopupLayoutProps): JSX.Element => {
  const {t} = useTranslation('common');

  return (
    <div className={css.popup}>
      <div className={css.heading}>
        <Text
          fontSize={Text.FONT_SIZE.SIZE_32}
          fontWeight={Text.FONT_WEIGHT.MEDIUM}
          fontFamily={Text.FONT_FAMILY.RUBIK}
        >
          <TextLocal id={'profile.clinic-info'} />
        </Text>
      </div>
      <Formik
        initialValues={{
          clinicName,
          userName,
          email,
          website,
        }}
        onSubmit={onSubmitClick}
        validationSchema={getValidationSchema(t)}
      >
        {({isSubmitting}) => (
          <Form className={css.form}>
            <div>
              <div className={css.formFields}>
                {/*@ts-ignore*/}
                <Input
                  width={Input.WIDTH.WIDTH_FULL}
                  name={'clinicName'}
                  placeholder={t('profile.clinic-name')}
                  isRequired
                />
                {/*@ts-ignore*/}
                <Input
                  width={Input.WIDTH.WIDTH_FULL}
                  name={'userName'}
                  placeholder={t('profile.clinic-login')}
                  isRequired
                />
                {/*@ts-ignore*/}
                <Input
                  width={Input.WIDTH.WIDTH_FULL}
                  name={'email'}
                  placeholder={t('profile.clinic-email')}
                  isRequired
                />
                {/*@ts-ignore*/}
                <Input
                  width={Input.WIDTH.WIDTH_FULL}
                  name={'website'}
                  placeholder={t('profile.clinic-web-site')}
                />
              </div>
              <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
              <div className={css.buttonsBlock}>
                <div className={css.cancelPseudoButton} onClick={onCancelClick}>
                  <Text
                    color={Text.COLOR.GREY}
                    fontWeight={Text.FONT_WEIGHT.MEDIUM}
                    fontSize={Text.FONT_SIZE.SIZE_16}
                  >
                    <TextLocal id={'create-course.cancel'} />
                  </Text>
                </div>
                <Button
                  color={Button.COLOR.PRIMARY}
                  height={Button.HEIGHT.REGULAR}
                  type={Button.TYPES.SUBMIT}
                  isLoading={isSubmitting}
                >
                  <TextLocal id={'profile.update'} />
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditClinicInfoPopupLayout;
